/**
 * jai1 tasks show <id|parent> [-j]
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { TaskService } from '../../services/task.service.js';
import {
    STATUS_ICONS,
    BLOCKED_ICON,
    PRIORITY_ICONS,
    PRIORITY_LABELS,
    type TaskShowOptions,
    type TaskStatusType,
} from '../../types/task.types.js';

export function createTaskShowCommand(): Command {
    return new Command('show')
        .description('Show task detail or all tasks under a parent')
        .argument('<query>', 'Task ID (T-001) or parent (feature/xxx)')
        .option('-j, --json', 'Output JSON')
        .action(async (query: string, options: TaskShowOptions) => {
            const service = new TaskService();

            if (query.startsWith('T-')) {
                // Show single task
                const task = await service.findById(query);
                if (!task) {
                    console.error(chalk.red(`❌ Task ${query} not found`));
                    process.exit(1);
                }

                if (options.json) {
                    console.log(JSON.stringify(task, null, 2));
                    return;
                }

                const { blocked, blockedBy } = await service.isBlocked(task);
                const statusIcon = blocked
                    ? BLOCKED_ICON
                    : STATUS_ICONS[task.status as TaskStatusType] || '📋';
                const priIcon = PRIORITY_ICONS[task.priority] || '🟡';
                const priLabel = PRIORITY_LABELS[task.priority] || 'Medium';

                console.log(chalk.bold(`\n📌 ${task.id}: ${task.title}\n`));
                console.log(`  ${chalk.dim('Status:')}     ${statusIcon} ${task.status}${blocked ? chalk.red(' (BLOCKED)') : ''}`);
                console.log(`  ${chalk.dim('Priority:')}   ${priIcon} P${task.priority} ${priLabel}`);
                if (task.parent) {
                    console.log(`  ${chalk.dim('Parent:')}     ${task.parent}`);
                }
                if (task.assigned_to) {
                    console.log(`  ${chalk.dim('Assigned:')}   @${task.assigned_to} (${task.claimed_at})`);
                }
                if (task.depends_on.length > 0) {
                    console.log(`  ${chalk.dim('Depends on:')} ${task.depends_on.join(', ')}`);
                    if (blocked) {
                        console.log(`  ${chalk.dim('Blocked by:')} ${chalk.red(blockedBy.join(', '))}`);
                    }
                }
                if (task.tags.length > 0) {
                    console.log(`  ${chalk.dim('Tags:')}       ${task.tags.join(', ')}`);
                }
                if (task.branch) {
                    console.log(`  ${chalk.dim('Branch:')}     ${task.branch}`);
                }
                if (task.notes) {
                    console.log(`  ${chalk.dim('Notes:')}      ${task.notes}`);
                }
                console.log(`  ${chalk.dim('Created:')}    ${task.created}`);
                console.log(`  ${chalk.dim('Updated:')}    ${task.updated}`);
                console.log();
            } else {
                // Show all tasks under parent
                const tasks = await service.filter({ parent: query });

                if (options.json) {
                    console.log(JSON.stringify(tasks, null, 2));
                    return;
                }

                if (tasks.length === 0) {
                    console.log(chalk.dim(`No tasks for parent: ${query}`));
                    return;
                }

                console.log(chalk.bold(`\n📋 ${query} (${tasks.length} tasks)\n`));

                const allTasks = await service.readAll();
                const doneIds = new Set(allTasks.filter((t) => t.status === 'done').map((t) => t.id));

                for (const task of tasks) {
                    const isBlocked =
                        task.status === 'todo' &&
                        task.depends_on.length > 0 &&
                        !task.depends_on.every((id) => doneIds.has(id));

                    const icon = isBlocked ? BLOCKED_ICON : STATUS_ICONS[task.status as TaskStatusType] || '📋';
                    let line = `  ${icon}  ${chalk.dim(task.id)}  P${task.priority}  ${task.title}`;
                    if (task.assigned_to) line += chalk.cyan(` @${task.assigned_to}`);
                    if (isBlocked) {
                        const bb = task.depends_on.filter((id) => !doneIds.has(id));
                        line += chalk.red(` (blocked: ${bb.join(', ')})`);
                    }
                    console.log(line);
                }
                console.log();
            }
        });
}
